import { ReactNode, ReactElement, WeakValidationMap } from 'react';
import PropTypes, { Validator } from 'prop-types';
import AttentionLarge from '@athena/forge-icons/dist/AttentionLarge';
import CriticalLarge from '@athena/forge-icons/dist/CriticalLarge';
import InfoLarge from '@athena/forge-icons/dist/InfoLarge';
import SuccessLarge from '@athena/forge-icons/dist/SuccessLarge';
import { ForgeIconComponent } from '@athena/forge-icons/dist/ForgeIconProps';
import { forgeClassHelper } from '../utils/classes';
const classes = forgeClassHelper({ name: 'inline-alert' });

export type InlineAlertType = 'info' | 'success' | 'attention' | 'critical';
// export const alertType: readonly AlertType[] = ['info', 'success', 'attention', 'critical'];

export type InlineAlertProps = {
  /** CSS class applied to the InlineAlert's root `div` */
  className?: string;
  /** Message to display */
  children?: ReactNode;
  /** Determines which icon displays next to the message. Can choose from one of `info`, `success`, `attention`, `critical`.
   * Type of `success` displays the message in green. */
  type?: InlineAlertType;
};

const Icons = {
  info: InfoLarge,
  success: SuccessLarge,
  attention: AttentionLarge,
  critical: CriticalLarge,
};

const InlineAlert = ({ className, children, type = 'info' }: InlineAlertProps): ReactElement => {
  const Icon: ForgeIconComponent = Icons[type];

  return (
    <div role="alert" {...classes({ extra: className })}>
      <Icon {...classes({ element: 'icon' })} />
      <span {...classes({ element: 'message', modifiers: { success: type === 'success' } })}>{children}</span>
    </div>
  );
};

/** Runtime prop-types for InlineAlert */
const inlineAlertPropTypes: WeakValidationMap<InlineAlertProps> = {
  /** CSS class applied to the InlineAlert's root `div` */
  className: PropTypes.string,

  /** Message to display */
  children: PropTypes.node,

  /** Determines which icon displays next to the message. Can choose from one of `info`, `success`, `attention`, `critical`.
   * Type of `success` displays the message in green. */
  type: PropTypes.oneOf(['info', 'success', 'attention', 'critical']) as Validator<InlineAlertType>,
};

InlineAlert.propTypes = inlineAlertPropTypes;

export default InlineAlert;
